import { useState } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Heading } from '@digdir/designsystemet-react';
import { PersonGroupIcon } from '@navikt/aksel-icons';
import { Routes } from '../../enums/Routes';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { SurveyDeadlineDialog } from '../../components/SurveyDeadlineDialog';
import type { PlannedSurveyListItem, SurveyListItem } from '../../domain/surveys/survey-list';
import { usePlannedSurveyList, useSurveyList } from './useSurveyList';
import { useSurveyActions } from './useSurveyActions';
import { TeamSelect } from '../../components/TeamSelect';
import { SurveyTabs } from './components/SurveyTabs';
import { ModuleFilter, type ModuleFilterValue } from './components/ModuleFilter';
import { EmptyPanel, ListPanel } from './components/ListPanel';
import { SurveyRow } from './components/SurveyRow';
import { PlannedSurveyRow } from './components/PlannedSurveyRow';
import { ConfirmDialog } from './components/ConfirmDialog';
import classes from './SurveysPage.module.css';

type OpenDialog =
  | { kind: 'deadline'; item: SurveyListItem }
  | { kind: 'delete'; item: SurveyListItem }
  | { kind: 'cancel'; item: PlannedSurveyListItem }
  | null;

/**
 * Undersøkelser for valgt team: sendte (/surveys/current) og planlagte (/surveys/planned).
 */
export const SurveysPage = () => {
  const { t, i18n } = useTranslation();
  const { selectedTeamId } = useSelectedUnitContext();
  const isPlannedView = useMatch(Routes.PlannedSurveys) !== null;
  const surveys = useSurveyList(selectedTeamId);
  const planned = usePlannedSurveyList(selectedTeamId);
  const actions = useSurveyActions(selectedTeamId);
  const [filter, setFilter] = useState<ModuleFilterValue>('all');
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const closeDialog = () => setDialog(null);

  const matchesFilter = (item: { module: string }) => filter === 'all' || item.module === filter;
  const hasTeam = Boolean(selectedTeamId);
  const formatList = (parts: string[]) =>
    new Intl.ListFormat(i18n.language === 'en' ? 'en' : 'nb', { type: 'conjunction' }).format(parts);

  return (
    <div className={classes.page}>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.content}>
        <header className={classes.header}>
          <div className={classes.heading}>
            <span className={classes.eyebrow}>{t('surveys.page.eyebrow')}</span>
            <Heading level={1} data-size="xl">
              {t('surveys.page.heading')}
            </Heading>
          </div>
          <div className={classes.headerActions}>
            <TeamSelect />
            {hasTeam && (
              <Button asChild>
                <Link to={Routes.CreateSurvey}>{t('surveys.page.createButton')}</Link>
              </Button>
            )}
          </div>
        </header>

        {hasTeam ? (
          <>
            <div className={classes.toolbar}>
              <SurveyTabs
                surveyCount={surveys.isLoading ? undefined : surveys.items.length}
                plannedCount={planned.isLoading ? undefined : planned.items.length}
              />
              {surveys.modules.length > 1 && (
                <ModuleFilter modules={surveys.modules} value={filter} onChange={setFilter} />
              )}
            </div>

            {isPlannedView ? (
              <PlannedList
                isLoading={planned.isLoading}
                items={planned.items.filter(matchesFilter)}
                isFiltered={filter !== 'all' && planned.items.length > 0}
                error={
                  planned.isError
                    ? { message: t('surveys.page.error.planned'), onRetry: planned.retry }
                    : undefined
                }
                onCancel={(item) => setDialog({ kind: 'cancel', item })}
              />
            ) : (
              <ListPanel
                columns={[t('surveys.page.columns.responses'), t('surveys.page.columns.result')]}
                isLoading={surveys.isLoading}
                error={
                  surveys.failedModules.length > 0
                    ? {
                        message: t('surveys.page.error.modules', {
                          modules: formatList(
                            surveys.failedModules.map((module) => t(`surveys.page.error.moduleNames.${module}`)),
                          ),
                        }),
                        onRetry: surveys.retryFailed,
                      }
                    : undefined
                }
                empty={
                  filter !== 'all' && surveys.items.length > 0
                    ? {
                        title: t('surveys.page.empty.noMatch.title'),
                        description: t('surveys.page.empty.noMatch.description'),
                      }
                    : {
                        title: t('surveys.page.empty.noSurveys.title'),
                        description: t('surveys.page.empty.noSurveys.description'),
                      }
                }
              >
                {surveys.items.filter(matchesFilter).map((item) => (
                  <SurveyRow
                    key={item.key}
                    item={item}
                    onCopyLink={actions.copyLink}
                    onEditDeadline={(survey) => setDialog({ kind: 'deadline', item: survey })}
                    onDelete={(survey) => setDialog({ kind: 'delete', item: survey })}
                  />
                ))}
              </ListPanel>
            )}
          </>
        ) : (
          <section className={classes.panel}>
            <EmptyPanel
              icon={<PersonGroupIcon />}
              title={t('surveys.page.empty.noTeam.title')}
              description={t('surveys.page.empty.noTeam.description')}
              action={
                <Button asChild>
                  <Link to={Routes.CreateTeam}>{t('surveys.page.empty.noTeam.action')}</Link>
                </Button>
              }
            />
          </section>
        )}
      </div>

      {dialog?.kind === 'deadline' && (
        <SurveyDeadlineDialog
          open
          surveyTitle={dialog.item.title}
          createdAt={dialog.item.startDate}
          currentDeadlineInDays={dialog.item.deadlineInDays ?? 0}
          isSubmitting={actions.isUpdatingDeadline}
          onSubmit={(days) => actions.updateDeadline(dialog.item, days, closeDialog)}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          open
          title={t('surveys.page.deleteDialog.title', { title: dialog.item.title })}
          text={t('surveys.page.deleteDialog.text')}
          confirmLabel={t('surveys.page.deleteDialog.confirm')}
          dismissLabel={t('surveys.page.deleteDialog.dismiss')}
          isPending={actions.isDeleting}
          onConfirm={() => actions.remove(dialog.item, closeDialog)}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'cancel' && (
        <ConfirmDialog
          open
          title={t('surveys.page.cancelDialog.title', { title: dialog.item.title })}
          text={t('surveys.page.cancelDialog.text')}
          confirmLabel={t('surveys.page.cancelDialog.confirm')}
          dismissLabel={t('surveys.page.cancelDialog.dismiss')}
          isPending={actions.isCancelling}
          onConfirm={() => actions.cancelPlanned(dialog.item, closeDialog)}
          onClose={closeDialog}
        />
      )}
    </div>
  );
};

type PlannedListProps = {
  isLoading: boolean;
  items: PlannedSurveyListItem[];
  isFiltered: boolean;
  error?: { message: string; onRetry: () => void };
  onCancel: (item: PlannedSurveyListItem) => void;
};

const PlannedList = ({ isLoading, items, isFiltered, error, onCancel }: PlannedListProps) => {
  const { t } = useTranslation();
  const empty = isFiltered ? 'noMatch' : 'noPlanned';

  return (
    <ListPanel
      columns={[t('surveys.page.columns.repeats'), t('surveys.page.columns.status')]}
      isLoading={isLoading}
      error={error}
      empty={{
        title: t(`surveys.page.empty.${empty}.title`),
        description: t(`surveys.page.empty.${empty}.description`),
      }}
    >
      {items.map((item) => (
        <PlannedSurveyRow key={item.key} item={item} onCancel={onCancel} />
      ))}
    </ListPanel>
  );
};
